import { useEffect, useRef, useState } from "react";
import { Play, Pause, Headphones } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

// Two synced audio elements; the toggle swaps which one is audible at the same position.
export const BeforeAfter = ({ example }) => {
  const a = useRef(null), b = useRef(null);
  const [side, setSide] = useState("after");
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const player = usePlayer();
  useEffect(() => { if (a.current) { a.current.muted = side !== "before"; b.current.muted = side !== "after"; } }, [side]);
  useEffect(() => () => { a.current?.pause(); b.current?.pause(); }, []);
  useEffect(() => { if (player.playing && a.current && !a.current.paused) { a.current.pause(); b.current.pause(); setPlaying(false); } }, [player.playing]);
  if (!example?.before_url || !example?.after_url) return null;
  const toggle = () => {
    if (playing) { a.current.pause(); b.current.pause(); return setPlaying(false); }
    if (player.playing) player.toggle();
    b.current.currentTime = a.current.currentTime;
    Promise.all([a.current.play(), b.current.play()]).then(() => setPlaying(true)).catch(() => {});
  };
  const seek = (e) => {
    const r = e.currentTarget.getBoundingClientRect(), x = ((e.clientX - r.left) / r.width) * dur;
    a.current.currentTime = x; b.current.currentTime = x; setT(x);
  };
  const onTime = () => { setT(a.current.currentTime); if (Math.abs(a.current.currentTime - b.current.currentTime) > 0.06) b.current.currentTime = a.current.currentTime; };
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-8">
      <div className="rounded-3xl border border-white/10 bg-[#1e2236] p-6 sm:p-8" data-testid="before-after">
        <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-300 inline-flex items-center gap-2"><Headphones className="h-4 w-4" /> Ascolta la differenza</p>
        <h2 className="font-display text-xl sm:text-2xl font-bold uppercase mt-2">Prima e dopo il nostro mastering</h2>
        <p className="text-sm text-zinc-400 mt-1">{example.title} · cambia tra Originale e Master mentre suona, il brano continua dallo stesso punto.</p>
        <audio ref={a} src={fileUrl(example.before_url)} preload="auto" muted={side !== "before"} onTimeUpdate={onTime} onLoadedMetadata={(e) => setDur(e.currentTarget.duration)} onEnded={() => setPlaying(false)} />
        <audio ref={b} src={fileUrl(example.after_url)} preload="auto" muted={side !== "after"} />
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <button onClick={toggle} className="h-14 w-14 shrink-0 rounded-full bg-emerald-500 text-[#151827] grid place-items-center hover:bg-emerald-400 transition-colors" aria-label={playing ? "Pausa" : "Riproduci"} data-testid="before-after-play-btn">
            {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
          </button>
          <div className="inline-flex rounded-full border border-white/20 p-1 self-start" role="group">
            {[["before", "Originale"], ["after", "Master"]].map(([k, l]) => (
              <button key={k} onClick={() => setSide(k)} data-testid={`before-after-${k}-btn`}
                className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${side === k ? (k === "after" ? "bg-emerald-500 text-[#151827]" : "bg-white text-[#151827]") : "text-zinc-300"}`}>{l}</button>
            ))}
          </div>
          <div className="flex-1 flex items-center gap-3">
            <span className="font-mono-tga text-xs text-zinc-400 w-10">{fmt(t)}</span>
            <div onClick={seek} className="flex-1 h-2 rounded-full bg-white/10 cursor-pointer overflow-hidden" data-testid="before-after-seek">
              <div className={`h-full ${side === "after" ? "bg-emerald-400" : "bg-zinc-300"} transition-[background-color]`} style={{ width: `${dur ? (t / dur) * 100 : 0}%` }} />
            </div>
            <span className="font-mono-tga text-xs text-zinc-400 w-10">{fmt(dur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
